import { useState, useEffect, useCallback } from 'react';
import {
  Tablet, Plus, Trash2, Edit3, Search, Shield, Clock,
  AlertTriangle, X, Gift
} from 'lucide-react';

const SKU_OPTIONS = [
  { value: 'TRIAL', label: 'Trial (30 days)', maxDevices: 2, maxProfiles: 1 },
  { value: 'STARTER', label: 'Starter', maxDevices: 5, maxProfiles: 3 },
  { value: 'PROFESSIONAL', label: 'Professional', maxDevices: 25, maxProfiles: 10 },
  { value: 'ENTERPRISE', label: 'Enterprise', maxDevices: 9999, maxProfiles: 999 },
];

const STATUS_COLORS = {
  TRIAL: { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
  ACTIVE: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
  EXPIRED: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
  REVOKED: { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af' },
  SUSPENDED: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
};

const formatDate = (dateString) => {
  if (!dateString) return '\u2014';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const EntryLicensesTab = ({ tenants }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    tenantId: '',
    sku: 'STARTER',
    maxDevices: 5,
    maxProfiles: 3,
    status: 'ACTIVE',
  });

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/entry/licenses', { credentials: 'include' });
      if (res.ok) {
        setLicenses(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch Entry licenses:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  const licensedTenantIds = licenses.map(l => l.tenantId);
  const unlicensedTenants = (tenants || []).filter(t => !licensedTenantIds.includes(t.id));

  const handleCreate = async () => {
    if (!formData.tenantId) return;
    try {
      const res = await fetch('/api/platform/entry/licenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setShowCreateModal(false);
        resetForm();
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleStartTrial = async (tenantId) => {
    try {
      const res = await fetch(`/api/platform/entry/licenses/${tenantId}/trial`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to start trial');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdate = async () => {
    if (!editingLicense) return;
    try {
      const res = await fetch(`/api/platform/entry/licenses/${editingLicense.tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sku: formData.sku,
          maxDevices: formData.maxDevices,
          maxProfiles: formData.maxProfiles,
          status: formData.status,
        }),
      });
      if (res.ok) {
        setEditingLicense(null);
        resetForm();
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to update license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRevoke = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/platform/entry/licenses/${deleteTarget.tenantId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteTarget(null);
        fetchLicenses();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const resetForm = () => {
    setFormData({ tenantId: '', sku: 'STARTER', maxDevices: 5, maxProfiles: 3, status: 'ACTIVE' });
  };

  const openEdit = (license) => {
    setFormData({
      tenantId: license.tenantId,
      sku: license.sku,
      maxDevices: license.maxDevices,
      maxProfiles: license.maxProfiles,
      status: license.status,
    });
    setEditingLicense(license);
  };

  const handleSkuChange = (sku) => {
    const skuDef = SKU_OPTIONS.find(s => s.value === sku);
    setFormData(prev => ({
      ...prev,
      sku,
      maxDevices: skuDef?.maxDevices || prev.maxDevices,
      maxProfiles: skuDef?.maxProfiles || prev.maxProfiles,
    }));
  };

  const filtered = licenses.filter(l => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return l.tenant?.name?.toLowerCase().includes(s) ||
      l.sku?.toLowerCase().includes(s) ||
      l.status?.toLowerCase().includes(s);
  });

  if (loading) {
    return <div className="platform-loading"><div className="platform-spinner" /></div>;
  }

  return (
    <div className="md-licenses-tab">
      {/* Stats Row */}
      <div className="md-lic-stats">
        <div className="md-lic-stat">
          <span className="md-lic-stat-value">{licenses.length}</span>
          <span className="md-lic-stat-label">Total Licenses</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#22c55e' }}>
            {licenses.filter(l => l.status === 'ACTIVE').length}
          </span>
          <span className="md-lic-stat-label">Active</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#a855f7' }}>
            {licenses.filter(l => l.status === 'TRIAL').length}
          </span>
          <span className="md-lic-stat-label">Trial</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#ef4444' }}>
            {licenses.filter(l => l.status === 'EXPIRED' || l.isExpired).length}
          </span>
          <span className="md-lic-stat-label">Expired</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="md-lic-toolbar">
        <div className="md-lic-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
          <Plus size={16} /> Assign License
        </button>
      </div>

      {/* Licenses Table */}
      {filtered.length === 0 ? (
        <div className="platform-empty">
          <div className="platform-empty-icon"><Tablet size={32} /></div>
          <h3>No Entry Licenses</h3>
          <p>Assign Serverli Entry licenses to tenants to enable tablet fleet management.</p>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
            <Plus size={16} /> Assign License
          </button>
        </div>
      ) : (
        <div className="md-lic-table-wrap">
          <table className="md-lic-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Devices</th>
                <th>Profiles</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lic => {
                const statusStyle = STATUS_COLORS[lic.isExpired ? 'EXPIRED' : lic.status] || STATUS_COLORS.REVOKED;
                const devicePercent = lic.maxDevices > 0
                  ? Math.round((lic.usage?.deviceCount || 0) / lic.maxDevices * 100)
                  : 0;
                const isOverLimit = (lic.usage?.deviceCount || 0) > lic.maxDevices;

                return (
                  <tr key={lic.id}>
                    <td>
                      <div className="md-lic-tenant">
                        <span className="md-lic-tenant-name">{lic.tenant?.name || 'Unknown'}</span>
                        <span className="md-lic-tenant-id">{lic.tenantId?.slice(0, 8)}...</span>
                      </div>
                    </td>
                    <td>
                      <span className="md-lic-sku">{lic.sku}</span>
                    </td>
                    <td>
                      <span className="md-lic-status" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                        {lic.isExpired ? 'EXPIRED' : lic.status}
                        {lic.status === 'TRIAL' && lic.trialDaysRemaining !== null && !lic.isExpired && (
                          <span className="md-lic-trial-days">{lic.trialDaysRemaining}d left</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="md-lic-usage">
                        <span className={isOverLimit ? 'md-lic-over' : ''}>
                          {lic.usage?.deviceCount || 0} / {lic.maxDevices >= 9999 ? '\u221E' : lic.maxDevices}
                        </span>
                        <div className="md-lic-bar">
                          <div
                            className={`md-lic-bar-fill ${isOverLimit ? 'over' : devicePercent > 80 ? 'warn' : ''}`}
                            style={{ width: `${Math.min(100, devicePercent)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      {lic.usage?.profileCount || 0} / {lic.maxProfiles >= 999 ? '\u221E' : lic.maxProfiles}
                    </td>
                    <td>
                      {lic.status === 'TRIAL' && lic.trialEndsAt
                        ? formatDate(lic.trialEndsAt)
                        : lic.expiresAt
                          ? formatDate(lic.expiresAt)
                          : 'Never'}
                    </td>
                    <td>
                      <div className="md-lic-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(lic)} title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(lic)} title="Revoke">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="platform-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><Plus size={18} /> Assign Entry License</h3>
              <button onClick={() => setShowCreateModal(false)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="form-group">
                <label>Tenant</label>
                <select
                  className="form-select"
                  value={formData.tenantId}
                  onChange={e => setFormData(prev => ({ ...prev, tenantId: e.target.value }))}
                >
                  <option value="">Select tenant...</option>
                  {unlicensedTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-select"
                  value={formData.sku}
                  onChange={e => handleSkuChange(e.target.value)}
                >
                  {SKU_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="md-lic-form-row">
                <div className="form-group">
                  <label>Max Devices</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxDevices}
                    onChange={e => setFormData(prev => ({ ...prev, maxDevices: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Profiles</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxProfiles}
                    onChange={e => setFormData(prev => ({ ...prev, maxProfiles: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!formData.tenantId}>
                Assign License
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingLicense && (
        <div className="platform-modal-overlay" onClick={() => setEditingLicense(null)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><Edit3 size={18} /> Edit License - {editingLicense.tenant?.name}</h3>
              <button onClick={() => setEditingLicense(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-select"
                  value={formData.sku}
                  onChange={e => handleSkuChange(e.target.value)}
                >
                  {SKU_OPTIONS.filter(s => s.value !== 'TRIAL').map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              </div>
              <div className="md-lic-form-row">
                <div className="form-group">
                  <label>Max Devices</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxDevices}
                    onChange={e => setFormData(prev => ({ ...prev, maxDevices: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Profiles</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxProfiles}
                    onChange={e => setFormData(prev => ({ ...prev, maxProfiles: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingLicense(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="platform-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Revoke License</h3>
              <button onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will revoke the Serverli Entry license for this tenant. They will lose access to all tablet fleet management features.
              </p>
              <div className="platform-delete-item">{deleteTarget.tenant?.name}</div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRevoke}>Revoke License</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryLicensesTab;
